import { ValidationSchemas, lookupConfig } from "./formConstants";
import Box from "@mui/material/Box";
import { format } from "date-fns";

export const TYPE_OF_CONNECTION_FIELDS = [
  "type_single_family",
  "type_multifamily",
  "type_rv_slip",
  "type_commercial",
];

export const CONNECTION_ROLE_FIELDS = ["role_owner", "role_builder"];

/* ╭──────────────────────────  PUBLIC FORM  ───────────────────────────╮ */
export const newWaterServiceConnectionFormConfig = [
  {
    title: "Notices",
    step: "Notices",
    fields: [
      {
        name: "connection_fee_subHeader",
        type: "subHeader",
        label: "Please Read Carefully",
        content: (
          <Box
            sx={{
              mt: 3,
              mb: 2,
              p: 2,
              backgroundColor: "#f3f6f9",
              borderLeft: "6px solid #5995CF",
              borderRadius: 1,
              fontSize: "0.95rem",
              lineHeight: 1.7,
            }}
          >
            <strong>
              It is the responsibility of the water service supplier
            </strong>{" "}
            to submit this completed Water Service Connection form{" "}
            <strong>and $1000 connection fee</strong> to the District. Upon
            receiving the completed form and fee, the District will process and
            send a <strong>Certificate of Confirmation</strong> to both the
            water service customer and the water service supplier. The
            Certificate of Confirmation{" "}
            <u>must be received prior to activating the new service</u>.
          </Box>
        ),
      },
    ],
  },
  {
    title: "Contact Information",
    step: "Contact Info",
    fields: [
      /* owner / builder toggle – single-select checkboxes */
      {
        name: "connection_role_subHeader",
        type: "subHeader",
        label: "Relationship to Connection",
      },
      {
        name: "role_owner",
        label: "Owner",
        type: "checkbox",
        exclusiveGroup: "connectionRole",
        validation: ValidationSchemas.boolean,
      },
      {
        name: "role_builder",
        label: "Builder",
        type: "checkbox",
        exclusiveGroup: "connectionRole",
        validation: ValidationSchemas.boolean,
      },
      {
        name: "contact_info_subHeader",
        type: "subHeader",
        label: "Contact Information",
      },
      {
        name: "first_name",
        label: "First Name",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "last_name",
        label: "Last Name",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "company_name",
        label: "Company Name",
        type: "text",
        validation: ValidationSchemas.string,
      },
      {
        name: "mail_phone",
        label: "Phone",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "street_email",
        label: "Email",
        type: "text",
        validation: ValidationSchemas.requiredEmail,
        infoTooltip: "Email address is used only to send confirmation.",
      },
      /* mailing address */
      {
        name: "owner_contact_subHeader",
        type: "subHeader",
        label: "Mailing Address",
      },
      {
        name: "mail_address",
        label: "Mailing Address",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "mail_city",
        label: "City",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "mail_state",
        label: "State",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "mail_zip",
        label: "Zip Code",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
    ],
  },
  {
    title: "New Connection Information",
    step: "New Connection Info",
    fields: [
      {
        name: "mailing_address_subHeader",
        type: "subHeader",
        label: "Connection Location",
      },
      {
        name: "street_address",
        label: "Street Address",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "street_unit",
        label: "Unit #",
        type: "text",
        validation: ValidationSchemas.string,
      },
      {
        name: "street_city",
        label: "City",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "street_state",
        label: "State",
        type: "text",
        validation: ValidationSchemas.requiredString,
        defaultValue: "TX",
      },
      {
        name: "street_subdivision",
        label: "Subdivision Name",
        type: "text",
        validation: ValidationSchemas.string,
      },

      /* ───── Type of Connection (exclusive checkboxes) ───── */
      {
        name: "type_of_connection_subHeader",
        type: "subHeader",
        label: "Type of Connection",
        content: "Check One",
      },
      {
        name: "type_single_family",
        label: "Residential – Single Family",
        type: "checkbox",
        exclusiveGroup: "typeOfConnection",
        validation: ValidationSchemas.boolean,
      },
      {
        name: "type_multifamily",
        label: "Residential – Multifamily",
        infoTooltip: "Includes duplex, apartment, etc.",
        type: "checkbox",
        exclusiveGroup: "typeOfConnection",
        validation: ValidationSchemas.boolean,
      },
      {
        name: "type_rv_slip",
        label: "RV Slip",
        infoTooltip: "Contact HTGCD office for fee",
        type: "checkbox",
        exclusiveGroup: "typeOfConnection",
        validation: ValidationSchemas.boolean,
      },
      {
        name: "type_commercial",
        label: "Commercial",
        type: "checkbox",
        infoTooltip: "Includes hotel, BnB, office, restaurant, hospital, etc.",
        exclusiveGroup: "typeOfConnection",
        validation: ValidationSchemas.boolean,
      },

      {
        name: "connection_details_subHeader",
        type: "subHeader",
        label: "Connection Details",
      },
      /* service provider & date */
      {
        name: "pws",
        label: "Water Service Provider",
        type: "selectSingle",
        lookupTable: lookupConfig.pwsOperators,
        validation: ValidationSchemas.positiveInt,
      },
      {
        name: "meter_size",
        label: "Meter Size (inches)",
        type: "text",
        validation: ValidationSchemas.string,
      },
      {
        name: "date_of_connection",
        label: "Date of Connection",
        type: "date",
      },
    ],
  },
  {
    title: "Signature",
    step: "Signature",
    fields: [
      {
        name: "applicant_acknowledge",
        label:
          "I acknowledge that it is the responsibility of the owner/applicant to pay all bank fees for notice of insufficient funds on a returned check.",
        type: "radioBoolean",
        validation: ValidationSchemas.requiredTrueBoolean,
      },
      {
        name: "applicant_signed_name",
        label: "Signature",
        infoTooltip:
          "By typing your name, you are signing this form electronically.",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "applicant_signed_date",
        label: "Date Signed",
        type: "date",
        validation: ValidationSchemas.requiredDate,
        defaultValue: format(new Date(), "yyyy-MM-dd"), // Default to today
      },
    ],
  },
  {
    title: "Comments",
    step: "Comments",
    fields: [
      {
        name: "comments",
        label: "Comments / Notes",
        type: "text",
        validation: ValidationSchemas.string,
      },
    ],
  },
];

/* ╭───────────────────────────────  ADMIN  ─────────────────────────────╮ */
export const adminWaterServiceConnectionFormConfig = [
  {
    title: "Administration",
    subtitle:
      "Internal fields for staff to review, track, and approve pending water service connections. These are never shown on the public form.",
    step: "Admin",
    adminOnly: true,
    fields: [
      /* record meta */
      {
        name: "pws_connection_ndx",
        label: "Record ID",
        type: "text",
        readOnly: true,
      },
      {
        name: "date_received",
        label: "Date Received",
        type: "dateTime",
        readOnly: true,
      },
      { name: "entered_by", label: "Entered By", type: "text", readOnly: true },
      {
        name: "date_entered",
        label: "Date Entered",
        type: "dateTime",
        readOnly: true,
      },

      /* payment / accounting */
      { name: "paid", label: "Amount Paid ($)", type: "text" },
      { name: "check_number", label: "Check #", type: "text" },
      { name: "date_sent_receipt", label: "Receipt Sent", type: "dateTime" },

      /* approval / denial status */
      {
        name: "subHeader_review_approval_status",
        type: "subHeader",
        label: "Review & Approval Status",
      },
      {
        name: "connection_approved",
        label: "Approval Status",
        type: "radioBoolean",
        defaultValue: null,
        optionLabels: {
          trueLabel: "Approved",
          falseLabel: "Denied",
          nullLabel: "Pending",
        },
        infoTooltip:
          "This is the final approval for the water service connection. Selecting and saving this will finalize the record.",
        validation: ValidationSchemas.boolean.nullable().optional(),
      },
      {
        name: "dt_approved",
        label: "Date Approved",
        type: "dateTime",
        readOnly: true,
      },
      {
        name: "approved_by_user",
        label: "Approved By",
        type: "text",
        readOnly: true,
      },
      {
        name: "dt_denied",
        label: "Date Denied",
        type: "dateTime",
        readOnly: true,
      },
      {
        name: "denied_by_user",
        label: "Denied By",
        type: "text",
        readOnly: true,
      },
    ],
  },
];
